import { AlertCircle, CheckCircle2, Info, X } from "lucide-react";
import { Toast as ToastPrimitive } from "radix-ui";
import * as React from "react";

import { cn } from "@/lib/utils";

export type ToastTone = "error" | "success" | "info";

/** Shape and icon carry the meaning too, never colour alone. */
const TONES: Record<ToastTone, { className: string; icon: React.ReactNode; label: string }> = {
  error: {
    className: "border-destructive/30 bg-destructive-soft text-destructive",
    icon: <AlertCircle size={20} />,
    label: "Erreur",
  },
  success: {
    className: "border-success/30 bg-success-soft text-success-foreground",
    icon: <CheckCircle2 size={20} />,
    label: "Succès",
  },
  info: {
    className: "border-info/30 bg-info-soft text-info-foreground",
    icon: <Info size={20} />,
    label: "Information",
  },
};

export const ToastProviderPrimitive = ToastPrimitive.Provider;

export function ToastViewport({ className }: { className?: string }) {
  return (
    <ToastPrimitive.Viewport
      className={cn(
        // Sits above the phone tab bar, bottom-right once there is room.
        "fixed bottom-20 left-1/2 z-50 flex w-[calc(100vw-2rem)] max-w-md -translate-x-1/2 flex-col gap-2 outline-none",
        "md:bottom-4 md:left-auto md:right-4 md:translate-x-0",
        className,
      )}
    />
  );
}

export function Toast({
  tone = "info",
  message,
  onOpenChange,
}: {
  tone?: ToastTone;
  message: string;
  onOpenChange: (open: boolean) => void;
}) {
  const { className, icon, label } = TONES[tone];

  return (
    <ToastPrimitive.Root
      open
      onOpenChange={onOpenChange}
      className={cn(
        "flex items-start gap-3 rounded-xl border p-4 shadow-lg",
        "data-[state=open]:animate-in data-[state=open]:slide-in-from-bottom-2",
        "data-[state=closed]:animate-out data-[state=closed]:fade-out",
        "data-[swipe=end]:animate-out data-[swipe=end]:fade-out",
        className,
      )}
    >
      <span aria-hidden className="mt-0.5 shrink-0">
        {icon}
      </span>

      <div className="min-w-0 flex-1">
        <ToastPrimitive.Title className="sr-only">{label}</ToastPrimitive.Title>
        <ToastPrimitive.Description className="text-[15px] font-medium">{message}</ToastPrimitive.Description>
      </div>

      <ToastPrimitive.Close
        aria-label="Fermer"
        className="shrink-0 rounded-md p-1 opacity-70 transition hover:opacity-100"
      >
        <X size={18} />
      </ToastPrimitive.Close>
    </ToastPrimitive.Root>
  );
}
